import { Add, Close, Refresh, Search } from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { IssueTypeIcon } from '../../components/elements/IssueTypeIcon';
import SkeletonLoader from '../../components/elements/SkeletonLoader';
import { WarningIcon } from '../../components/elements/CustomIcons';
import IssueSuggestions from '../../components/project-dashboard/test-execution/test-execution-body/IssueSuggestions';
import CreateJiraIssueForm from '../../components/elements/common/traceability/CreateIssueForm';
import SearchIssuesModalComponent from '../../components/elements/common/traceability/SearchIssuesModal';
import { useNotification } from '../../hooks/NotificationContext';
import { getTestCycleKeyFromLocation } from '../../lib/executionUtils';
import { generateNotificationUrl } from '../../lib/notificationUtils';
import { getPriorityColor } from '../../lib/utils';
import { useAccess } from '../../hooks/useAccess';
import {
  associateIssuesBulk,
  fetchAssociatedIssues,
  removeAssociatedIssue,
} from '../../modules/associations';
import { useProject } from '../../context/ProjectContext';
import QUERY_KEYS from '../../query/queryKeys';
import ConfirmationModal from '../../ui/primitives/ConfirmationModal';
import { CountChip } from '../../ui/primitives/Chips';
import DetailsCard from '../../ui/layout/DetailsCard';
import ActionButton from '../../ui/primitives/ActionButton';
import { Guard } from '../Guard';

const LinkedIssue = ({
  entityId,
  entityType,
  isShowSuggestion,
  data,
  entityKey,
  showWarning = false,
}) => {
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [issueKey, setIssueKey] = useState([]);
  const [step, setStep] = useState(1);
  const [selectedProjectFromSearch, setSelectedProjectFromSearch] = useState(null);
  const [submittingAdd, setSubmittingAdd] = useState(false);
  const { addNotification } = useNotification();
  const { projectId } = useParams();
  const location = useLocation();
  const testCycleKey = getTestCycleKeyFromLocation(location);
  const queryClient = useQueryClient();
  const forceRefreshRef = useRef(false);

  const { canAccess } = useAccess();
  const canManageIntegrations = canAccess('ManageJiraConfig');;

  const { currentProject, isLoading: dataLoading } = useProject();
  const linkedJiraProjects = currentProject?.linked_jira_projects || [];

  const {
    data: linkedIssues = [],
    isLoading,
    error,
    isFetching,
    refetch: refetchLinkedIssues,
  } = useQuery({
    queryKey: [QUERY_KEYS.issues.list, entityId, projectId],
    queryFn: async () => {
      const currentForceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      return fetchAssociatedIssues(entityId, projectId, currentForceRefresh);
    },
    enabled: !!entityId && !!currentProject && linkedJiraProjects.length > 0,
    select: (res) =>
      res.issues?.map((issue) => ({
        key: issue.key,
        id: issue.jira_issue_id,
        summary: issue.summary,
        status: issue.status,
        issue_type: issue.issue_type,
        priority: issue.priority,
        jiraProjectName: issue.jira_project_name,
      })) || [],
  });

  const linkedIssueIds = linkedIssues.map((i) => i.id);

  const handleShowConfirm = (issue) => {
    setSelectedIssue(issue);
    setShowConfirm(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await removeAssociatedIssue(entityId, selectedIssue.id);
      addNotification('success', 'Issue removed successfully.');
      refetchLinkedIssues();
    } catch {
      addNotification('danger', 'Failed to delete linked issue.');
    } finally {
      setShowConfirm(false);
      setSelectedIssue(null);
    }
  };

  const handleAddIssues = async (issues) => {
    const otherData =
      entityType === 'Execution'
        ? { testCaseKey: data?.key, testCycleKey }
        : null;
    const entityUrl = generateNotificationUrl(
      entityType,
      projectId,
      entityKey,
      'traceability',
      otherData,
    );
    const associations = {
      issues: issues.map((issue) => ({
        id: issue.id,
        key: issue.key,
        priority: issue.priority,
        projectId: issue.projectId,
      })),
      entityId,
      entityType,
      gainssProjectId: projectId,
      entityUrl,
      entityKey,
    };
    setSubmittingAdd(true);
    try {
      await associateIssuesBulk(associations, projectId);
      addNotification('success', 'Issue linked successfully.');
      handleRefresh();
      if (entityType === 'Execution') {
        setIssueKey(issues?.map((issue) => issue.key));
        setStep(2);
      } else {
        setShowSearchModal(false);
      }
    } catch (err) {
      addNotification('danger', err.message || 'Failed to create the Jira issue.');
    } finally {
      setSubmittingAdd(false);
    }
  };

  const handleRefresh = () => {
    forceRefreshRef.current = true;
    queryClient.refetchQueries({
      queryKey: [QUERY_KEYS.issues.list, entityId, projectId],
      exact: true,
    });
  };

  const handleShowCreateModal = () => {
    setShowCreateModal(true);
    setShowSearchModal(false);
  };

  const cardTitle = (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Typography variant="h6">Linked Issues</Typography>
      {linkedIssues?.length > 0 && <CountChip label={linkedIssues.length} />}
      {!isLoading && showWarning && linkedIssues?.length === 0 && (
        <WarningIcon tooltipText="Link defects to document this failed execution." />
      )}
    </Stack>
  );

  const cardActions = (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {!isLoading && (
        <ActionButton
          icon={<Refresh fontSize="small" />}
          tooltip="Refresh linked issues"
          onClick={handleRefresh}
          loading={isFetching}
          intent="icon"
          size="small"
          sx={{ minWidth: 16 }}
        />
      )}
      {currentProject?.integrated_platform &&
        linkedJiraProjects?.length > 0 && (
          <Guard policy="ManageTraceability">
            <ActionButton
              icon={<Add fontSize="small" />}
              tooltip="Create New Issue"
              onClick={() => setShowCreateModal(true)}
              intent="icon"
              size="small"
              sx={{ minWidth: 16 }}
            />
            <ActionButton
              icon={<Search fontSize="small" />}
              tooltip="Search & Link Issues"
              onClick={() => setShowSearchModal(true)}
              intent="icon"
              size="small"
              sx={{ minWidth: 16 }}
            />
          </Guard>
        )}
    </Stack>
  );

  const renderBody = () => {
    if (dataLoading) return <SkeletonLoader rowCount={2} />;

    if (!currentProject?.integrated_platform) {
      return (
        <Alert severity="warning">
          {canManageIntegrations
            ? 'No project management tool is integrated. Please set up the integration to enable issue creation and linking.'
            : 'No project management tool is integrated. Please contact your organization admin to configure the integration.'}
        </Alert>
      );
    }

    if (linkedJiraProjects.length === 0) {
      return (
        <Alert severity="warning">
          {canManageIntegrations
            ? 'This project is not linked to any external project. Please link the project to enable issue creation and linking.'
            : 'This project is not linked to any external project. Please contact your organization admin to link it.'}
        </Alert>
      );
    }

    return (
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error.message}
          </Alert>
        )}

        {isLoading ? (
          <SkeletonLoader />
        ) : linkedIssues?.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 3 }}
          >
            No linked issues found.
          </Typography>
        ) : (
          <List disablePadding>
            {linkedIssues.map((issue, index) => (
              <Box key={issue.id}>
                <ListItem
                  disablePadding
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 0.5,
                    py: 1,
                    gap: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'background-color 0.12s ease',
                    '&:hover': { backgroundColor: 'action.hover' },
                    '& .remove-btn': { opacity: 0, transition: 'opacity 0.15s ease' },
                    '&:hover .remove-btn': { opacity: 1 },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Box sx={{ flexShrink: 0 }} title={issue.issue_type}>
                      <IssueTypeIcon issueType={issue.issue_type} />
                    </Box>

                    <Link
                      href={`${currentProject?.jira_site_url}/browse/${issue.key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open issue in new tab"
                      variant="body2"
                      fontWeight={700}
                      underline="hover"
                      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      {issue.key}
                    </Link>

                    <Typography
                      variant="body2"
                      color="text.primary"
                      title={issue.summary}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}
                    >
                      {issue.summary?.length > 100
                        ? `${issue.summary.slice(0, 100)}…`
                        : issue.summary}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ flexShrink: 0 }}
                  >
                    {issue.jiraProjectName && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {issue.jiraProjectName}
                      </Typography>
                    )}

                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          backgroundColor: getPriorityColor(issue.priority) || 'grey.300',
                          borderRadius: '2px',
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {issue.priority}
                      </Typography>
                    </Stack>

                    <Chip
                      label={issue.status}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        borderRadius: '4px',
                        backgroundColor: 'grey.200',
                        color: 'text.secondary',
                        letterSpacing: '0.04em',
                      }}
                    />

                    <Guard policy="ManageTraceability">
                      <Tooltip title="Remove linked issue" arrow placement="left">
                        <IconButton
                          className="remove-btn"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowConfirm(issue);
                          }}
                          sx={{
                            color: 'error.main',
                            backgroundColor: 'error.light',
                            borderRadius: '50%',
                            width: 22,
                            height: 22,
                            '&:hover': { backgroundColor: 'error.main', color: '#fff' },
                          }}
                        >
                          <Close sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Tooltip>
                    </Guard>
                  </Stack>
                </ListItem>

                {index < linkedIssues.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
        <Guard policy="ManageTraceability">
        {isShowSuggestion && (
          <IssueSuggestions
            entityId={entityId}
            handleAddIssues={handleAddIssues}
            linkedIssueIds={linkedIssueIds}
            linkedJiraProjects={linkedJiraProjects}
            currentProject={currentProject}
          />
        )}
        </Guard>
      </Box>
    );
  };

  return (
    <>
      <DetailsCard title={cardTitle} actions={cardActions}>
        {renderBody()}
      </DetailsCard>

      <SearchIssuesModalComponent
        show={showSearchModal}
        setShowSearchModal={setShowSearchModal}
        handleAddIssues={handleAddIssues}
        linkedIssueIds={linkedIssueIds}
        currentProject={currentProject}
        step={step}
        issueKey={issueKey}
        testCaseData={data}
        setStep={setStep}
        entityKey={entityKey}
        submitting={submittingAdd}
        setSubmitting={setSubmittingAdd}
        linkedJiraProjects={linkedJiraProjects}
        handleShowCreateModal={handleShowCreateModal}
        setSelectedProjectFromSearch={setSelectedProjectFromSearch}
      />
      <CreateJiraIssueForm
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        fetchLinkedIssues={handleRefresh}
        entityId={entityId}
        entityType={entityType}
        currentProject={currentProject}
        testCaseData={data}
        entityKey={entityKey}
        linkedJiraProjects={linkedJiraProjects}
        selectedProjectFromSearch={selectedProjectFromSearch}
      />
      <ConfirmationModal
        show={showConfirm}
        handleConfirm={handleConfirmRemove}
        handleClose={() => setShowConfirm(false)}
        title="Remove Linked Issue"
        body="Are you sure you want to remove this linked issue?"
      />
    </>
  );
};

export default LinkedIssue;